import type { LoginCredentials } from '@/scopes/auth/domain/types'
import { Button } from '@sunwebgroup/uds-ui/button'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import { FormProvider, useForm } from 'react-hook-form'
import { useIntl } from '@/intl'
import { useAuthActions, useAuthRecaptcha } from '@/scopes/auth/infrastructure/dependencies'
import { useAuthStore } from '@/scopes/auth/infrastructure/useAuthStore'
import { getIsSubmitDisabled } from '@/scopes/auth/pages/selectors/authSelectors'
import { PasswordInput } from '@/scopes/common/components/FormElements/PasswordInput'
import { EMAIL_INPUT_PATTERN } from '@/scopes/common/components/FormElements/patterns'
import { TextInput } from '@/scopes/common/components/FormElements/TextInput'
import { AuthFormLayout } from '../AuthFormLayout'
import { LoginErrorBanner } from './LoginErrorBanner'
import styles from './LoginForm.module.css'

export function LoginForm() {
  const intl = useIntl()
  const methods = useForm<LoginCredentials>({ defaultValues: { email: '', password: '' } })
  const { loginRequested, createAccountFormOpened, resetPasswordFormOpened } = useAuthActions()
  const submitStatus = useAuthStore(state => state.submitStatus)
  const isSubmitDisabled = useAuthStore(getIsSubmitDisabled)
  const recaptchaRef = useAuthRecaptcha()

  async function handleSubmit(credentials: LoginCredentials) {
    const recaptchaToken = await recaptchaRef.current?.execute()

    return loginRequested({ ...credentials, recaptchaToken })
  }

  const isLoadingStatus = submitStatus === 'loading'

  return (
    <FormProvider {...methods}>
      <AuthFormLayout
        onSubmit={methods.handleSubmit(handleSubmit)}
        title={intl.formatMessage({ id: 'auth_login_form_title' })}
        subtitle={intl.formatMessage({ id: 'auth_login_form_subtitle' })}
        dataQa="auth-login-form"
      >
        <>
          <div className={styles.fields}>
            <LoginErrorBanner />
            <TextInput
              name="email"
              label={intl.formatMessage({ id: 'auth_form_email_label' })}
              autoComplete="username"
              required
              requiredErrorMessage={intl.formatMessage({ id: 'auth_form_email_required_error' })}
              pattern={EMAIL_INPUT_PATTERN}
              patternErrorMessage={intl.formatMessage({ id: 'auth_form_email_pattern_error' })}
              disabled={isLoadingStatus}
            />
            <div className={styles.passwordGroup}>
              <PasswordInput
                name="password"
                label={intl.formatMessage({ id: 'auth_form_password_label' })}
                required
                requiredErrorMessage={intl.formatMessage({ id: 'auth_form_password_required_error' })}
                showPasswordLabel={intl.formatMessage({ id: 'auth_form_password_show_label' })}
                hidePasswordLabel={intl.formatMessage({ id: 'auth_form_password_hide_label' })}
                autoComplete="current-password"
                disabled={isLoadingStatus}
              />
              <Typography as="button" type="button" variant="body-s-regular-tight" className={styles.link} onClick={resetPasswordFormOpened} disabled={isLoadingStatus}>
                {intl.formatMessage({ id: 'auth_login_form_forgot_password_label' })}
              </Typography>
            </div>
          </div>
          <div className={styles.footer}>
            <Button type="submit" variant="primary" fullWidth isLoading={isLoadingStatus} disabled={isSubmitDisabled}>
              {intl.formatMessage({ id: 'auth_login_form_submit_label' })}
            </Button>
            <Typography as="p" variant="body-s-regular-tight">
              {intl.formatMessage({ id: 'auth_login_form_no_account_label' })}
              {' '}
              <Typography as="button" type="button" variant="body-s-regular-tight" className={styles.link} onClick={createAccountFormOpened} disabled={isLoadingStatus}>
                {intl.formatMessage({ id: 'auth_login_form_create_account_label' })}
              </Typography>
            </Typography>
          </div>
        </>
      </AuthFormLayout>
    </FormProvider>
  )
}
